/**
 * ChromeBar — Serac surface switcher.
 *
 * Top horizontal bar that lets visitors jump between the three Serac
 * surfaces: serac.build (product), dashboard.serac.build (the actual
 * portal / this app), seraclabs.dev (company).
 *
 * Mirrors seraclabs/src/components/ChromeBar.astro so all three surfaces
 * share the same chrome.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

interface ChromeBarProps {
  /** Which tab to mark as active. Default: 'app' (we're inside the dashboard). */
  active?: 'build' | 'docs' | 'app' | 'labs';
}

const tabs = [
  { k: 'build', url: 'serac.build', href: 'https://serac.build', dot: true,  labelKey: 'chrome.product' },
  { k: 'docs',  url: 'docs.serac.build', href: 'https://docs.serac.build', dot: true,  labelKey: 'chrome.docs' },
  { k: 'app',   url: 'dashboard.serac.build', href: 'https://dashboard.serac.build/login', dot: true,  labelKey: 'chrome.dashboard' },
  { k: 'labs',  url: 'seraclabs.dev', href: 'https://seraclabs.dev', dot: false, labelKey: 'chrome.company' },
] as const;

export default function ChromeBar({ active = 'app' }: ChromeBarProps) {
  const { t } = useTranslation(['nav', 'common']);
  return (
    <div className="serac-chrome-bar" style={chrome}>
      <span style={brand}>SERAC ·</span>
      {tabs.map((tab_) => {
        const isActive = active === tab_.k;
        const isExternal = tab_.href.startsWith('http');
        return (
          <a
            key={tab_.k}
            href={tab_.href}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            style={{
              ...tab,
              ...(isActive ? tabActive : null),
            }}
          >
            {tab_.dot && <span style={dot} />}
            <span>{tab_.url}</span>
            <span style={tabLabel}>· {t(tab_.labelKey)}</span>
          </a>
        );
      })}
      {/* Inline rule: hide the horizontal scrollbar in WebKit/Blink so the
          bar reads clean — items still scroll on touchpad, wheel, and swipe.
          Firefox uses scrollbarWidth on the container style. */}
      <style>{`.serac-chrome-bar::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
}

const chrome: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 16px',
  background: 'var(--chrome-bar-bg)',
  borderBottom: '1px solid var(--border)',
  fontSize: 12,
  height: 40,
  flexShrink: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  scrollbarWidth: 'none',
  position: 'relative',
  zIndex: 50,
};

const brand: CSSProperties = {
  color: 'var(--fg-dim, oklch(0.40 0.008 230))',
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.12em',
  paddingRight: 4,
  flexShrink: 0,
};

const tab: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 10px',
  borderRadius: 6,
  color: 'var(--fg-muted, oklch(0.56 0.006 230))',
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  letterSpacing: '0.04em',
  border: '1px solid transparent',
  textDecoration: 'none',
  flexShrink: 0,
  transition: 'background 140ms ease, color 140ms ease, border-color 140ms ease',
  cursor: 'pointer',
};

const tabActive: CSSProperties = {
  background: 'var(--ink-100, oklch(0.14 0.012 230))',
  color: 'var(--fg, oklch(0.94 0.003 230))',
  borderColor: 'var(--border, oklch(0.18 0.012 230))',
};

const dot: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'var(--ice-500, oklch(0.66 0.150 205))',
  display: 'inline-block',
};

const tabLabel: CSSProperties = {
  color: 'var(--fg-dim, oklch(0.40 0.008 230))',
  marginLeft: 4,
};
